import { useEffect, useState } from "react";
import type { EmailPreflightWarning } from "@/types/AttachmentDrop";
import { emailPreflight } from "@/lib/api/handlers/AttachmentHandling";

export interface AttachmentPreflightDescriptor {
  eventId?: string;
  emailTemplateId?: string;
  guestId?: string;
  /** Only fetch when true (e.g. the send modal is open with a template picked). */
  enabled?: boolean;
}

interface AttachmentPreflightResult {
  warnings: EmailPreflightWarning[] | null;
  loading: boolean;
  error: boolean;
}

/**
 * Fetches the send-modal attachment preflight — which of the template's
 * attachments would NOT reach this recipient's email (wallet signing/icon,
 * calendar start date, condition mismatch, size guards). Refetches on
 * template/recipient switch via the dep key. FAILS SOFT (error=true,
 * warnings=null): a preflight blip must never block or degrade the send flow
 * — this is warn-only UI, especially load-bearing for TEST sends which write
 * no audit row.
 */
export function useAttachmentEmailPreflight({
  eventId,
  emailTemplateId,
  guestId,
  enabled = true,
}: AttachmentPreflightDescriptor): AttachmentPreflightResult {
  const [warnings, setWarnings] = useState<EmailPreflightWarning[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  // Stable dependency key — mirrors useEmailOptInBreakdown.
  const depKey = JSON.stringify({ eventId, emailTemplateId, guestId, enabled });

  useEffect(() => {
    let cancelled = false;
    if (!enabled || !eventId || !emailTemplateId || !guestId) {
      setWarnings(null);
      setLoading(false);
      setError(false);
      return;
    }
    setLoading(true);
    setError(false);
    (async () => {
      const res = await emailPreflight(eventId, emailTemplateId, guestId);
      if (cancelled) return;
      if (res && typeof res === "object" && "warnings" in res) {
        setWarnings(res.warnings ?? []);
      } else {
        setError(true);
        setWarnings(null);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [depKey]);

  return { warnings, loading, error };
}
